import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button as AntButton, DatePicker, Input } from 'antd'
import { default as CustomButton } from 'components/Button'
import Icon from 'components/Icon'
import PageTable from 'components/PageTable'
import SortableColumnTitle from 'components/SortableColumnTitle'
import useClient from 'hooks/useClient'
import { DATE_FORMAT, PAYMENT_METHODS, PERMISSIONS, ICONS } from 'utils/constants'
import { getPlans, getCustomerSubscriptions } from 'redux/actions/subscriptions'
import ExportSubscriptionsModal from 'components/ExportSubscriptionsModal'

const { RangePicker } = DatePicker

const serializeTableFilters = (filters) =>
  JSON.stringify(
    Object.fromEntries(Object.entries(filters || {}).filter(([, value]) => value != null)),
  )

const CustomerSubscriptions = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isClientCan } = useClient()

  const canRead = isClientCan(PERMISSIONS.READ_SUBSCRIPTIONS)
  const [isExportModalVisible, setIsExportModalVisible] = useState(false)

  const { plans, customerSubscriptions, customerSubscriptionsTotal, isGetLoading } = useSelector(
    (state) => state.subscriptions,
  )

  const [isNeedLoadData, setIsNeedLoadData] = useState(false)

  const [tableFilter, setTableFilter] = useState({
    planId: null,
    subscriptionSapId: null,
    startDate: null,
    endDate: null,
    createdDate: null,
    subscriptionStatus: ['Active'],
  })

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [sortType, setSortType] = useState('CREATED_AT')
  const [sortOrder, setSortOrder] = useState('DESC')

  // local UI state for filter dropdowns
  const [sapIdFilter, setSapIdFilter] = useState('')
  const [startDatesFilter, setStartDatesFilter] = useState(null)
  const [endDatesFilter, setEndDatesFilter] = useState(null)

  useEffect(() => {
    dispatch(getPlans())
  }, [dispatch])

  const refresh = () => {
    dispatch(
      getCustomerSubscriptions({
        take: pageSize,
        page,
        search: search ? search : null,
        sortType,
        sortOrder,
        filters: serializeTableFilters(tableFilter),
      }),
    )
    setIsNeedLoadData(false)
  }

  useEffect(() => {
    if (page !== 1) setPage(1)
    setIsNeedLoadData(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableFilter, search])

  useEffect(() => {
    setIsNeedLoadData(true)
  }, [tableFilter, search, sortType, sortOrder, page, pageSize])

  useEffect(() => {
    if (!canRead) return
    isNeedLoadData && refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNeedLoadData, canRead])

  const columns = useMemo(() => {
    const planFilters = (plans || []).map((p) => ({
      text: `${p.preferredPlan?.descriptionEn || p.preferredPlan?.name || ''}`,
      value: p.id,
    }))

    return [
      { title: '№', dataIndex: 'id', key: 'id', width: '60px', align: 'center' },
      {
        title: (props) => (
          <SortableColumnTitle {...props} title='Customer' id='customerName' />
        ),
        key: 'customerName',
        dataIndex: ['user', 'fullName'],
        render: (v) => v || '-',
      },
      {
        title: 'Customer SAP ID',
        key: 'customerSapId',
        dataIndex: ['user', 'sapId'],
        render: (v) => v || '-',
      },
      {
        title: (props) => <SortableColumnTitle {...props} title='Mobile' id='mobile' />,
        key: 'mobile',
        dataIndex: ['user', 'phoneNumber'],
        render: (v) => v || '-',
      },
      {
        title: 'Plan',
        key: 'planId',
        filters: planFilters,
        filterSearch: true,
        render: (_, r) => {
          // const title = r?.plan?.product?.title
          const pref = r?.plan?.preferredPlan?.descriptionEn || r?.plan?.preferredPlan?.name
          // if (!title && !pref) return '-'
          // return `${title || ''}${pref ? ` (${pref})` : ''}`.trim()
          return pref || '-'
        },
      },
      {
        title: 'Subscription Code',
        dataIndex: 'subscriptionSapId',
        key: 'subscriptionSapId',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <>
            <div className={'container'}>
              <Input
                placeholder='SAP ID'
                value={sapIdFilter}
                onChange={(e) => setSapIdFilter(e.target.value)}
              />
            </div>
            <div className={'ant-table-filter-dropdown-btns'}>
              <AntButton
                type={'link'}
                size={'small'}
                disabled={!sapIdFilter}
                onClick={() => setSapIdFilter('')}
              >
                Reset
              </AntButton>
              <AntButton
                type={'primary'}
                size={'small'}
                onClick={() => {
                  setSelectedKeys(sapIdFilter ? [sapIdFilter] : null)
                  confirm({ closeDropdown: true })
                }}
              >
                OK
              </AntButton>
            </div>
          </>
        ),
        render: (v, record) => (
          <span
            style={{ color: '#1677ff', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/main/orders?search=${encodeURIComponent(v || '')}`)
            }}
          >
            {v || '-'}
          </span>
        ),
      },
      // {
      //   title: 'Subscription Amount',
      //   dataIndex: 'documentTotal',
      //   key: 'subscriptionAmount',
      //   render: (v) => (v != null ? Number(v).toFixed(3) : '-'),
      // },
      {
        title: 'Next Delivery Date',
        dataIndex: 'scheduleAt',
        key: 'nextDeliveryDate',
        render: (v) => (v ? moment(v).format(DATE_FORMAT) : '-'),
      },
      {
        title: 'Start Date',
        dataIndex: 'startDate',
        key: 'startDate',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <>
            <div className={'container'}>
              <RangePicker
                format={DATE_FORMAT}
                value={startDatesFilter}
                onChange={(dates) => {
                  if (!dates) return setStartDatesFilter(null)
                  setStartDatesFilter([dates[0].startOf('day'), dates[1].endOf('day')])
                }}
              />
            </div>
            <div className={'ant-table-filter-dropdown-btns'}>
              <AntButton
                type={'link'}
                size={'small'}
                disabled={!startDatesFilter}
                onClick={() => setStartDatesFilter(null)}
              >
                Reset
              </AntButton>
              <AntButton
                type={'primary'}
                size={'small'}
                onClick={() => {
                  if (startDatesFilter) {
                    setSelectedKeys([
                      startDatesFilter[0].format('YYYY-MM-DD'),
                      startDatesFilter[1].format('YYYY-MM-DD'),
                    ])
                  } else {
                    setSelectedKeys(null)
                  }
                  confirm({ closeDropdown: true })
                }}
              >
                OK
              </AntButton>
            </div>
          </>
        ),
        render: (v) => (v ? moment(v).format(DATE_FORMAT) : '-'),
      },
      {
        title: 'End Date',
        dataIndex: 'endDate',
        key: 'endDate',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <>
            <div className={'container'}>
              <RangePicker
                format={DATE_FORMAT}
                value={endDatesFilter}
                onChange={(dates) => {
                  if (!dates) return setEndDatesFilter(null)
                  setEndDatesFilter([dates[0].startOf('day'), dates[1].endOf('day')])
                }}
              />
            </div>
            <div className={'ant-table-filter-dropdown-btns'}>
              <AntButton
                type={'link'}
                size={'small'}
                disabled={!endDatesFilter}
                onClick={() => setEndDatesFilter(null)}
              >
                Reset
              </AntButton>
              <AntButton
                type={'primary'}
                size={'small'}
                onClick={() => {
                  if (endDatesFilter) {
                    setSelectedKeys([
                      endDatesFilter[0].format('YYYY-MM-DD'),
                      endDatesFilter[1].format('YYYY-MM-DD'),
                    ])
                  } else {
                    setSelectedKeys(null)
                  }
                  confirm({ closeDropdown: true })
                }}
              >
                OK
              </AntButton>
            </div>
          </>
        ),
        render: (v) => (v ? moment(v).format(DATE_FORMAT) : '-'),
      },
      {
        title: 'Status',
        dataIndex: 'subscriptionStatus',
        key: 'subscriptionStatus',
        filters: [
          { text: 'Active', value: 'Active' },
          { text: 'Cancelled', value: 'Cancelled' },
          { text: 'Completed', value: 'Completed' },
        ],
        defaultFilteredValue: ['Active'],
      },
      {
        title: 'Payment Method',
        dataIndex: 'paymentMethod',
        key: 'paymentMethod',
        filters: [
          // { text: PAYMENT_METHODS.CASH, value: 'CASH' },
          { text: PAYMENT_METHODS.KNET, value: 'KNET' },
          // { text: PAYMENT_METHODS.COUPON, value: 'COUPON' },
          { text: PAYMENT_METHODS.APPLE_PAY, value: 'APPLE_PAY' },
          { text: PAYMENT_METHODS.CREDIT_CARD, value: 'CREDIT_CARD' },
          { text: PAYMENT_METHODS.WALLET, value: 'WALLET' },
          // { text: PAYMENT_METHODS.ECOUPON, value: 'E-COUPON' },
          // { text: PAYMENT_METHODS.SUBSCRIPTION, value: 'SUBSCRIPTION' },
        ],
        render: (paymentMethod, record) => {
          const walletPaymentMethod =
            record?.walletAmount && paymentMethod && paymentMethod !== 'E-WALLET'
              ? `${PAYMENT_METHODS.WALLET} + ${PAYMENT_METHODS[paymentMethod]}`
              : PAYMENT_METHODS.WALLET

          if (record?.walletAmount) {
            return walletPaymentMethod
          }

          return paymentMethod && PAYMENT_METHODS[paymentMethod.replace('-', '')]
        },
      },
      {
        title: 'Paused',
        dataIndex: 'isPaused',
        key: 'isPaused',
        filters: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        render: (v) => (v ? 'Yes' : 'No'),
      },
      {
        title: 'Auto Renew',
        dataIndex: 'autoRenew',
        key: 'autoRenew',
        filters: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        render: (v) => (v ? 'Yes' : 'No'),
      },
    ]
  }, [plans, sapIdFilter, startDatesFilter, endDatesFilter])

  const adaptSortOrder = (antTableOrder) => {
    if (antTableOrder === 'ascend') return 'ASC'
    if (antTableOrder === 'descend') return 'DESC'
    return 'DESC'
  }

  const tableChangeHandler = (pagination, filter, sorter) => {
    setTableFilter(filter)

    // only accept known sort keys
    if (sorter?.columnKey && ['CREATED_AT', 'START_DATE', 'END_DATE'].includes(sorter.columnKey)) {
      setSortType(sorter.columnKey)
      setSortOrder(adaptSortOrder(sorter.order))
    }

    setPage(pagination.current)
    setPageSize(pagination.pageSize)
  }

  return (
    <>
      <PageTable
        pageTitle='Customer Subscriptions'
        baseData={customerSubscriptions}
        columns={columns}
        loading={isGetLoading}
        action={
          <>
            <CustomButton type="primary" onClick={() => setIsExportModalVisible(true)}>
              <Icon icon={ICONS.UPLOAD} size={24} color={'#ffffff'} />
              <span className="btn-text">{t('app.export')}</span>
            </CustomButton>
          </>
        }
        onRow={(record) => ({
          onClick: () =>
            navigate(`/main/subscriptions/customer-subscriptions/view/${record.id}`, {
              state: { subscription: record },
            }),
        })}
        pagination={{
          current: page,
          pageSize,
          total: customerSubscriptionsTotal,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          showSizeChanger: true,
        }}
        onChange={tableChangeHandler}
        sortDirections={['ascend', 'descend', 'ascend']}
        searchHandler={(v) => setSearch(v.trim())}
      />
      <ExportSubscriptionsModal
        isVisible={isExportModalVisible}
        handleCancel={() => setIsExportModalVisible(false)}
        fileDownloadedHandler={() => setIsExportModalVisible(false)}
      />
    </>
  )
}

export default CustomerSubscriptions
